import {hasJapanese, isKanji} from '../shared/kana.ts';
import {
  loadSettings,
  saveSettings,
  type AnkiSettings,
} from './anki/settings.ts';
import type {Meta, Entry} from '../shared/types.ts';
import {Dict} from './dict.ts';
import {fill, h, join, searchLink} from './dom.ts';
import {headword} from './forms.ts';
import {HistoryStore, HistoryView} from './history-view.ts';
import {applyUpdate, newVersion, onNewVersion} from './update.ts';
import {
  addTranslations,
  lookupEnglish,
  renderEnglish,
  type EnglishWord,
} from './english.ts';
import {language, rememberLanguage, t, translatePage} from './i18n.ts';

// The page's own text, before anything else is drawn.
translatePage();
import type {HistoryItem} from './history.ts';
import {RadicalPicker} from './radicals.ts';
import {
  renderEntry,
  renderKanji,
  renderSentence,
  type EntryActions,
  resultsHeading,
} from './render.ts';
import {search, type SearchResult} from './search.ts';
import {
  applyDisplaySettings,
  loadDisplaySettings,
  saveDisplaySetting,
  loadLanguage,
  saveLanguage,
  type DisplaySettings,
} from './settings.ts';
import {openStore, type Store} from './store/store.ts';
import {WordTools} from './word-tools.ts';
import {reloadIfStale} from './stale.ts';
import {OfflineDictionary, createOfflinePanel} from './offline/offline.ts';
import {fillJapanese, linkJapanese} from './japanese.ts';
import {loadSpeller, Speller} from './spell.ts';
import {buildQueue} from './study/model.ts';
import {loadAccount} from './account.ts';
import {Sync} from './sync.ts';
import {attachUndo} from './undo.ts';

/** Most kanji shown in the sidebar. */
const MAX_SIDEBAR_KANJI = 10;

/** The home page's example searches, and what each one shows. */
function examples(): [string, string][] {
  return [
    ['食べる', t('a word')],
    ['taberu', t('romaji')],
    ['dog', t('English')],
    ['行きたくなかった', t('an inflected word')],
    ['猫が好きです', t('a sentence')],
    ['たべ', t('the start of a word')],
  ];
}

function $<T extends HTMLElement>(id: string): T {
  return document.getElementById(id) as T;
}

const form = $<HTMLFormElement>('search-form');
const input = $<HTMLInputElement>('q');
const radicalToggle = $<HTMLButtonElement>('radical-toggle');
const radicalPanel = $<HTMLElement>('radical-panel');
const handwritingToggle = $<HTMLButtonElement>('handwriting-toggle');
const handwritingPanel = $<HTMLElement>('handwriting-panel');
const photoToggle = $<HTMLButtonElement>('photo-toggle');
const photoPanel = $<HTMLElement>('photo-panel');
const photoInput = $<HTMLInputElement>('photo-input');
const settingsToggle = $<HTMLButtonElement>('settings-toggle');
const settingsPanel = $<HTMLElement>('settings-panel');
const content = $<HTMLElement>('content');
const dataInfo = $<HTMLElement>('data-info');
const undo = attachUndo(input);
/** Defaults until the store is open. */
let display = loadDisplaySettings(undefined);
let store: Store | undefined;
/** Saving the whole dictionary for offline use; set once the app starts. */
let offline: OfflineDictionary | undefined;
/** Running while signed in. */
let sync: Sync | undefined;

function stopSync() {
  sync?.stop();
  sync = undefined;
}

/** Starts syncing if signed in. In the background: nothing waits for it. */
async function startSync(opened: Store) {
  if (!opened.persistent) return;
  const account = await loadAccount();
  if (account.kind !== 'signed-in') return;
  sync = new Sync(opened, account.account.id);
  sync.onSignedOut = () => (sync = undefined);
  await sync.start();
}
const historyStore = new HistoryStore();
/**
 * The history column beside the page, on screens wide enough for it (CSS
 * shows it there and shows the home page's copy otherwise).
 */
const historyColumn = new HistoryView(historyStore, true);
$<HTMLElement>('history-column').append(historyColumn.element);

/** meta.json, once loaded: the data sets' versions. */
let meta: Meta | undefined;
const dataBase = new URL('data/', document.baseURI);

async function loadJson(path: string): Promise<unknown> {
  const url = new URL(path, dataBase);
  // Data files are immutable per version (the server caches them for a
  // year), so the URL carries their data set's version: "ent/0001.json" is
  // in "ent", "radk.json" is "radk". meta.json says what the versions are,
  // so it's always revalidated. (Data built before per-set versions has
  // only the JMdict version.)
  const set = path.includes('/')
    ? path.split('/')[0]
    : path.replace(/\.json$/, '');
  const version = meta?.versions?.[set] ?? meta?.version;
  if (version) url.searchParams.set('v', version);
  const res = await fetch(url, path === 'meta.json' ? {cache: 'no-cache'} : {});
  if (!res.ok) throw new Error(`${url}: ${res.status}`);
  return res.json();
}

function searchUrl(
  q: string,
  extra: Record<string, string | number> = {},
): string {
  const params = new URLSearchParams({q});
  for (const [k, v] of Object.entries(extra)) params.set(k, String(v));
  return `?${params}`;
}

/** A word as history shows it: its headword, reading and first meanings. */
function wordSnapshot(entry: Entry): NonNullable<HistoryItem['word']> {
  const word = headword(entry);
  return {
    text: word.text,
    ...(word.reading && {reading: word.reading}),
    meaning: entry.s[0].g.slice(0, 3).join('; '),
  };
}

/** Remembers a search in the history, with a snapshot of its top result. */
function recordHistory(query: string, result: SearchResult) {
  const top = result.words[0];
  if (!top && !result.sentence) return;
  historyStore.add({
    q: query,
    t: Date.now(),
    ...(top && {word: wordSnapshot(top.entry)}),
  });
}

function renderHome(): HTMLElement {
  return h(
    'div',
    {class: 'home'},
    // For narrow screens, where there's no history column.
    display.history && new HistoryView(historyStore).element,
    h(
      'p',
      {class: 'intro'},
      t(
        'Search in English, Japanese, or romaji. Paste a sentence to see its words.',
      ),
    ),
    h(
      'ul',
      {class: 'examples-list'},
      examples().map(([q, what]) =>
        h(
          'li',
          null,
          searchLink(q, h('span', {lang: 'ja'}, q)),
          h('span', {class: 'hint'}, ` ${what}`),
        ),
      ),
    ),
  );
}

/** Kanji for the sidebar: those in the query, else those in the top results. */
function sidebarKanji(query: string, result: SearchResult): string[] {
  const source = hasJapanese(query)
    ? query
    : result.words
        .slice(0, 5)
        .map(w => headword(w.entry).text)
        .join('');
  return [...new Set(Array.from(source).filter(isKanji))].slice(
    0,
    MAX_SIDEBAR_KANJI,
  );
}

/** What a search found: its Japanese words, and the English word. */
async function lookUp(dict: Dict, query: string, pages: number) {
  const [result, found] = await Promise.all([
    search(dict, query, {pages}),
    // A nice-to-have: the search still shows without it.
    lookupEnglish(dict, query).catch(() => []),
  ]);
  // An English word the 英和 data doesn't have: JMdict's Japanese words for
  // it, explained in Japanese.
  const english =
    display.englishJapanese && pages === 1
      ? await addTranslations(
          dict,
          query,
          found,
          result.words.map(w => w.entry),
        ).catch(() => found)
      : found;
  return {result, english};
}

function foundNothing(result: SearchResult, english: EnglishWord[]) {
  return result.total === 0 && !result.sentence && english.length === 0;
}

/** A misspelled search: searched as the word it surely meant, or suggestions. */
type Spelling = {corrected: string; typed: string} | {suggestions: string[]};

function renderSpelling(spelling: Spelling | undefined): HTMLElement | false {
  if (!spelling) return false;
  if ('corrected' in spelling) {
    return h(
      'p',
      {class: 'spelling'},
      fill(t('Showing results for {word}. Search instead for {typed}.'), {
        word: h('strong', null, spelling.corrected),
        typed: h(
          'a',
          {href: searchUrl(spelling.typed, {exact: 1})},
          spelling.typed,
        ),
      }),
    );
  }
  return h(
    'p',
    {class: 'spelling'},
    fill(t('Did you mean {words}?'), {
      words: h(
        'span',
        null,
        join(
          spelling.suggestions.map(w => searchLink(w, h('strong', null, w))),
          ', ',
        ),
      ),
    }),
  );
}

/** Why a search shows nothing: nothing matched, or its dictionaries are off. */
function noResults(query: string, english: EnglishWord[]): string {
  if (english.length && !display.englishJapanese && !display.englishEnglish) {
    return t(
      'English words are turned off. To see them, turn on Japanese meanings or English definitions in Settings.',
    );
  }
  if (!display.meanings && !display.japanese) {
    return t(
      'Japanese words are turned off. To see them, turn on English meanings or Japanese definitions in Settings.',
    );
  }
  return t("Sorry, couldn't find anything matching {query}.", {query});
}

async function renderResults(
  dict: Dict,
  query: string,
  result: SearchResult,
  pages: number,
  /** the English word searched for, explained in Japanese (英和) */
  english: EnglishWord[] = [],
  spelling?: Spelling,
): Promise<HTMLElement> {
  // Each switch in Settings is a dictionary. With both Japanese-word ones
  // off (English → Japanese only, say), Japanese words aren't shown at all.
  const japaneseWords = display.meanings || display.japanese;
  if (!japaneseWords) {
    result = {...result, words: [], total: 0, sentence: undefined};
  }
  const englishShown =
    english.some(
      w =>
        display.englishJapanese && (w.entries.length || w.translations?.length),
    ) || english.some(w => display.englishEnglish && w.definitions.length);
  // What each kanji's parts mean: a nice-to-have.
  const parts = await dict.parts().catch(() => undefined);
  const kanji = await Promise.all(
    sidebarKanji(query, result).map(async k => {
      const [info, strokes] = await Promise.all([
        dict.kanji(k),
        // Stroke order is a nice-to-have: don't fail the page without it.
        dict.strokes(k).catch(() => undefined),
      ]);
      return info && {info, strokes};
    }),
  );
  const [sentenceBar, sentenceWords] = result.sentence
    ? renderSentence(dict, result, entryActions)
    : [];
  const main =
    sentenceWords ??
    h(
      'section',
      {class: 'words'},
      renderSpelling(spelling),
      pages === 1 && renderEnglish(english),
      japaneseWords && resultsHeading(result),
      result.words.length === 0 &&
        !englishShown &&
        h('p', {class: 'no-results'}, noResults(query, english)),
      result.words.map(w => renderEntry(dict, w, entryActions)),
      result.words.length < result.total &&
        h(
          'a',
          {class: 'more-words', href: searchUrl(query, {p: pages + 1})},
          t('More words'),
        ),
    );
  const found = kanji.filter(k => k !== undefined);
  return h(
    'div',
    {class: 'results'},
    sentenceBar,
    h(
      'div',
      {class: 'columns'},
      main,
      found.length > 0 &&
        h(
          'aside',
          {class: 'kanji-sidebar'},
          h(
            'h2',
            {class: 'results-heading'},
            t('Kanji'),
            h(
              'span',
              {class: 'count'},
              ` — ${t('{n} found', {n: found.length})}`,
            ),
          ),
          found.map(k => renderKanji(k.info, k.strokes, parts)),
        ),
    ),
  );
}

/** Star / known / note controls; set once the store is open. */
let wordTools: WordTools | undefined;
/** The add-to-Anki control; set when Anki is connected. */
let ankiActions: EntryActions | undefined;

/** Controls added to every entry. */
const entryActions: EntryActions = entry =>
  h(
    'div',
    {class: 'word-actions'},
    ankiActions?.(entry),
    wordTools?.actions(entry),
  );

let currentSearch = 0;

/**
 * Shows the page for the current URL. `record` adds the search to the
 * history (not done for back/forward, which revisit it).
 */
async function route(dict: Dict, record = false) {
  const params = new URLSearchParams(location.search);
  const query = params.get('q')?.trim() ?? '';
  const pages = Math.max(1, Number(params.get('p') ?? 1) || 1);
  // An undo step, so what was typed before the search can be brought back.
  undo.set(query);
  historyColumn.setCurrent(query);
  document.title = query
    ? t('{query} - g-sho', {query})
    : t('g-sho — Japanese dictionary');

  if (params.has('import') && store) {
    document.title = t('Import - g-sho');
    const id = ++currentSearch;
    const view = await import('./study/import-view.ts');
    if (id !== currentSearch) return;
    const file = pendingImport;
    pendingImport = undefined;
    content.replaceChildren(view.renderImport(store, dict, file));
    return;
  }
  const study = params.get('study');
  if (study !== null && store) {
    document.title = t('Study - g-sho');
    const id = ++currentSearch;
    if (study === 'browse' || study === 'stats') {
      const page =
        study === 'browse'
          ? (await import('./study/browse.ts')).renderBrowser(store)
          : (await import('./study/stats.ts')).renderStats(store);
      if (id === currentSearch) content.replaceChildren(page);
      return;
    }
    const view = await import('./study/view.ts');
    if (id !== currentSearch) return;
    content.replaceChildren(
      study
        ? view.renderSession(store, dict, study)
        : view.renderDecks(store, dict),
    );
    return;
  }
  if (!query) {
    content.replaceChildren(renderHome());
    return;
  }
  const id = ++currentSearch;
  content.setAttribute('aria-busy', 'true');
  try {
    let shown = query;
    let {result, english} = await lookUp(dict, query, pages);
    // Nothing anywhere: maybe a misspelling ("beterment"). A clear best guess
    // is searched for; otherwise the likeliest are offered.
    let spelling: Spelling | undefined;
    if (pages === 1 && !params.has('exact') && foundNothing(result, english)) {
      const speller = await loadSpeller(dict);
      const suggestions = speller?.suggest(query) ?? [];
      const winner = Speller.clearWinner(suggestions);
      const corrected = winner && (await lookUp(dict, winner, pages));
      if (
        winner &&
        corrected &&
        !foundNothing(corrected.result, corrected.english)
      ) {
        ({result, english} = corrected);
        shown = winner;
        spelling = {corrected: winner, typed: query};
      } else if (suggestions.length) {
        spelling = {suggestions: suggestions.slice(0, 3).map(s => s.word)};
      }
    }
    const view = await renderResults(
      dict,
      shown,
      result,
      pages,
      english,
      spelling,
    );
    if (id !== currentSearch) return;
    content.replaceChildren(view);
    void fillJapanese(dict, content);
    void linkJapanese(dict, content);
    if (record && pages === 1) recordHistory(shown, result);
    historyColumn.setCurrent(query);
  } catch (e) {
    if (id !== currentSearch || reloadIfStale(e)) return;
    console.error(e);
    content.replaceChildren(
      h(
        'p',
        {class: 'error'},
        // A network failure (not a server error) rejects fetch with a
        // TypeError; navigator.onLine alone isn't reliable.
        e instanceof TypeError || navigator.onLine === false
          ? t(
              'You’re offline, and this part of the dictionary isn’t saved on this device yet. Words you’ve looked up before work offline.',
            )
          : t('Something went wrong loading the dictionary. Try again.'),
      ),
    );
  } finally {
    if (id === currentSearch) content.removeAttribute('aria-busy');
  }
}

const backButton = $<HTMLButtonElement>('back-button');

function pageDepth(): number {
  return (history.state as {depth?: number} | null)?.depth ?? 0;
}

/**
 * The back button (shown only in the home-screen app, by CSS) appears when
 * there's an earlier g-sho page to go back to.
 */
function showBackButton() {
  backButton.hidden = pageDepth() === 0;
}

/** A file dropped or pasted, for the import page to pick up. */
let pendingImport: File | undefined;

/** Anki decks dropped or pasted anywhere open the import page. */
function setupImportDrop(dict: Dict) {
  const importable = (f: File) => /\.(apkg|colpkg|txt|tsv|csv)$/i.test(f.name);
  const root = document.documentElement.classList;
  const hasFiles = (e: DragEvent) => e.dataTransfer?.types.includes('Files');
  document.addEventListener('dragover', e => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    root.add('dropping');
  });
  document.addEventListener('dragleave', e => {
    if (!e.relatedTarget) root.remove('dropping');
  });
  document.addEventListener('drop', e => {
    root.remove('dropping');
    if (!hasFiles(e)) return;
    // Don't let the browser open the file instead.
    e.preventDefault();
    const file = [...(e.dataTransfer?.files ?? [])].find(importable);
    if (file) {
      pendingImport = file;
      navigate(dict, '?import');
    }
  });
  document.addEventListener('paste', e => {
    const file = [...(e.clipboardData?.files ?? [])].find(importable);
    if (file) {
      e.preventDefault();
      pendingImport = file;
      navigate(dict, '?import');
    }
  });
}

function navigate(dict: Dict, url: string, scroll = true, record = true) {
  // How many in-app pages deep we are, for the back button.
  history.pushState({depth: pageDepth() + 1}, '', url);
  showBackButton();
  if (scroll) window.scrollTo(0, 0);
  void route(dict, record);
}

/** Inserts text at the search box's cursor, as its own undo step. */
function insertAtCursor(text: string) {
  undo.insert(text);
}

/**
 * Makes a button toggle a panel, creating the panel's content the first
 * time it opens. Opening one panel closes the others.
 */
function setupPanel(
  toggle: HTMLButtonElement,
  panel: HTMLElement,
  create: () => Promise<HTMLElement>,
) {
  let created = false;
  toggle.addEventListener('click', async () => {
    const open = panel.hidden;
    openPanel(open ? panel : null);
    if (open && !created) {
      created = true;
      panel.replaceChildren(h('p', {class: 'panel-loading'}, t('Loading…')));
      try {
        panel.replaceChildren(await create());
      } catch (e) {
        created = false;
        if (reloadIfStale(e)) return;
        console.error(e);
        panel.replaceChildren(
          h('p', {class: 'error'}, t('Couldn’t load this panel.')),
        );
      }
    }
  });
}

const panels: [HTMLButtonElement, HTMLElement][] = [
  [photoToggle, photoPanel],
  [radicalToggle, radicalPanel],
  [handwritingToggle, handwritingPanel],
  [settingsToggle, settingsPanel],
];

/** Turns the add-to-Anki buttons on or off for the settings. */
async function applyAnki(dict: Dict, settings: AnkiSettings) {
  const notice = $<HTMLElement>('anki-notice');
  notice.hidden = true;
  ankiActions = settings.enabled
    ? (await import('./anki/controller.ts')).createAnkiActions(settings, dict, {
        // One notice for the page when Anki can't be reached.
        show(message, retry) {
          notice.replaceChildren(
            h('span', {class: 'anki-notice-mark', 'aria-hidden': 'true'}, '!'),
            h(
              'span',
              {title: message},
              t(
                'Anki isn’t reachable, so words can’t be added. Is Anki running?',
              ),
            ),
            h('button', {type: 'button', onclick: retry}, t('Retry')),
            h(
              'button',
              {
                type: 'button',
                onclick: async () => {
                  const off = {...loadSettings(), enabled: false};
                  saveSettings(off);
                  await applyAnki(dict, off);
                  void route(dict);
                },
              },
              t('Turn off Anki'),
            ),
          );
          notice.hidden = false;
        },
        hide() {
          notice.hidden = true;
        },
      })
    : undefined;
}

/** Shows only this panel (or none), and which toggle is on. */
function openPanel(panel: HTMLElement | null) {
  for (const [toggle, p] of panels) {
    p.hidden = p !== panel;
    toggle.setAttribute('aria-expanded', String(p === panel));
  }
  if (panel !== photoPanel) stopCamera();
}

/** The next search keeps the page where it is (a line picked in a photo). */
let keepScroll = false;

/** Turns the camera off when the photo panel closes. */
let stopCamera = () => {};

/**
 * The camera button opens the photo panel reading what the camera sees
 * (where there's no camera to use, it picks an image instead); the panel
 * loads on first use.
 */
function setupPhoto() {
  let panel: Promise<import('./ocr/panel.ts').PhotoPanel> | undefined;
  const open = async () => {
    openPanel(photoPanel);
    if (!panel) {
      photoPanel.replaceChildren(
        h('p', {class: 'panel-loading'}, t('Loading…')),
      );
      panel = import('./ocr/panel.ts').then(({PhotoPanel}) => {
        const p = new PhotoPanel(
          text => {
            undo.set(text);
            // Search it where the page is: no jump to the top.
            keepScroll = true;
            form.requestSubmit();
          },
          () => photoInput.click(),
        );
        stopCamera = () => p.freeze();
        return p;
      });
    }
    try {
      const p = await panel;
      photoPanel.replaceChildren(p.element);
      return p;
    } catch (e) {
      panel = undefined;
      if (reloadIfStale(e)) return;
      console.error(e);
      photoPanel.replaceChildren(
        h('p', {class: 'error'}, t('Couldn’t load this panel.')),
      );
      return;
    }
  };
  photoToggle.addEventListener('click', async () => {
    if (!photoPanel.hidden) openPanel(null);
    else if (!navigator.mediaDevices?.getUserMedia) photoInput.click();
    else await (await open())?.startCamera();
  });
  photoInput.addEventListener('change', async () => {
    const file = photoInput.files?.[0];
    photoInput.value = '';
    if (!file) return;
    await (await open())?.show(file);
  });
}

function setupPanels(dict: Dict) {
  setupPhoto();
  setupPanel(radicalToggle, radicalPanel, async () => {
    const picker = new RadicalPicker(await dict.radicals(), insertAtCursor);
    return picker.element;
  });
  setupPanel(handwritingToggle, handwritingPanel, async () => {
    // Loaded on demand: the recognizer and its model aren't needed until now.
    const {createHandwritingPanel} = await import('./handwriting/panel.ts');
    const hw = createHandwritingPanel(char => {
      insertAtCursor(char);
      hw.clear();
    });
    return hw.element;
  });
  setupPanel(settingsToggle, settingsPanel, async () => {
    const {createSettingsPanel} = await import('./settings-panel.ts');
    return createSettingsPanel(
      display,
      (key, on) => {
        if (store) saveDisplaySetting(store.settings, key, on);
        showDisplaySettings(dict, {...display, [key]: on});
      },
      async () => {
        const {createAnkiPanel} = await import('./anki/panel.ts');
        return createAnkiPanel(loadSettings(), async settings => {
          await applyAnki(dict, settings);
          void route(dict);
        });
      },
      async () =>
        (await import('./account-panel.ts')).createAccountPanel(
          () => sync,
          stopSync,
        ),
      offline ? createOfflinePanel(offline) : null,
      lang => {
        if (store) saveLanguage(store.settings, lang);
        rememberLanguage(lang);
        // Everything on the page is drawn in the old language.
        location.reload();
      },
    );
  });
}

/** The number on the Study button: cards to study now. */
function watchDue(opened: Store) {
  const badge = $<HTMLElement>('study-due');
  let timer: ReturnType<typeof setTimeout> | undefined;
  const show = () => {
    const n = buildQueue(opened).cards.length;
    badge.hidden = n === 0;
    badge.textContent = n > 99 ? '99+' : String(n);
    $<HTMLElement>('study-link').title =
      n === 0
        ? t('Study')
        : n === 1
          ? t('Study: {n} card to review now', {n})
          : t('Study: {n} cards to review now', {n});
  };
  const soon = () => {
    clearTimeout(timer);
    timer = setTimeout(show, 300);
  };
  for (const table of [opened.cards, opened.reviews, opened.decks])
    table.onChange(soon);
  // Learning cards come due by the minute.
  setInterval(show, 60_000);
  show();
}

function showDisplaySettings(
  dict: Dict | undefined,
  settings: DisplaySettings,
) {
  const historyChanged = settings.history !== display.history;
  // Which dictionaries are on decides what a search shows: search again.
  const dictionaries = (s: DisplaySettings) =>
    [s.meanings, s.japanese, s.englishJapanese, s.englishEnglish].join();
  const dictionariesChanged = dictionaries(settings) !== dictionaries(display);
  display = settings;
  applyDisplaySettings(display);
  if (dict) void fillJapanese(dict, content);
  document.documentElement.classList.toggle('hide-history', !display.history);
  if (dict && historyChanged && !location.search) void route(dict);
  else if (dict && dictionariesChanged && location.search) void route(dict);
}

function storage(): Storage | undefined {
  try {
    return localStorage;
  } catch {
    return undefined;
  }
}

async function main() {
  const opened = await openStore(storage());
  store = opened;
  // The language chosen on another device (or before this browser had a
  // copy of it): switch to it, once, before the page is in use.
  const chosen = loadLanguage(opened.settings);
  if (chosen && chosen !== language) {
    rememberLanguage(chosen);
    location.reload();
    return;
  }
  showDisplaySettings(undefined, loadDisplaySettings(opened.settings));
  historyStore.attach(opened.history);
  wordTools = new WordTools(opened);
  watchDue(opened);
  void startSync(opened);
  const dict = await Dict.open(loadJson);
  historyStore.attachStars(opened.marks, async wordId => {
    const entry = await dict.entry(wordId);
    if (!entry) return undefined;
    const word = wordSnapshot(entry);
    return {wordId, q: word.text, t: 0, word};
  });
  // Including changes synced from another device. A new language is used
  // from the next page on.
  opened.settings.onChange(() => {
    showDisplaySettings(dict, loadDisplaySettings(opened.settings));
    const lang = loadLanguage(opened.settings);
    if (lang) rememberLanguage(lang);
  });
  meta = dict.meta;
  dataInfo.textContent = t('JMdict {date} · {words} words · {kanji} kanji', {
    date: dict.meta.dictDate,
    words: dict.meta.entryCount.toLocaleString(),
    kanji: dict.meta.kanjiCount.toLocaleString(),
  });

  form.addEventListener('submit', e => {
    e.preventDefault();
    const q = input.value.trim();
    navigate(dict, q ? searchUrl(q) : location.pathname, !keepScroll);
    keepScroll = false;
  });

  // Handle in-app search links without a page load.
  document.addEventListener('click', e => {
    if (
      e.defaultPrevented ||
      e.button !== 0 ||
      e.metaKey ||
      e.ctrlKey ||
      e.shiftKey ||
      e.altKey
    ) {
      return;
    }
    const a = (e.target as Element).closest('a');
    // Links within the page (#word-3) just scroll.
    if (a?.getAttribute('href')?.startsWith('#')) return;
    if (
      !a ||
      a.origin !== location.origin ||
      a.pathname !== location.pathname ||
      !a.search
    ) {
      return;
    }
    e.preventDefault();
    // "More words" appends results below, so stay where the reader is.
    // Picking a word from the history doesn't move it to the top: the list
    // shouldn't reshuffle under you while you look back at earlier words.
    navigate(
      dict,
      a.search,
      !a.classList.contains('more-words'),
      !a.closest('.history'),
    );
  });

  window.addEventListener('popstate', () => {
    showBackButton();
    void route(dict);
  });
  backButton.addEventListener('click', () => history.back());
  showBackButton();
  setupPanels(dict);
  setupImportDrop(dict);
  await applyAnki(dict, loadSettings());
  // A search opened from a link counts as a lookup too.
  await route(dict, true);
  // The whole dictionary for offline use, in the background, once the
  // service worker (which serves it) is running.
  offline = new OfflineDictionary(dict.meta);
  void navigator.serviceWorker?.ready.then(() => offline?.autoStart());
}

/**
 * Offline use: the service worker (sw.ts) saves the app and the dictionary
 * data you use. Not in local dev, where it would get in the way of
 * rebuilds, unless turned on with ?sw (once; ?sw=0 turns it off).
 */
function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return;
  const local = ['localhost', '127.0.0.1'].includes(location.hostname);
  if (local) {
    const flag = new URLSearchParams(location.search).get('sw');
    try {
      if (flag !== null)
        localStorage.setItem('g-sho.sw', flag === '0' ? '' : '1');
      if (!localStorage.getItem('g-sho.sw')) return;
    } catch {
      return;
    }
  }
  navigator.serviceWorker
    .register('/sw.js')
    .then(reg => {
      // Fetch a new deploy's files in the background whenever the app
      // comes back to the front (watchForUpdates offers to switch).
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') void reg.update();
      });
    })
    .catch(e => {
      console.warn('service worker not registered', e);
    });

  // The app opens from saved files, so a new deploy arrives a moment later
  // (a new service worker takes over). Switch to it right away if nothing
  // has been done on the page yet; otherwise the logo offers it.
  const hadController = !!navigator.serviceWorker.controller;
  let used = false;
  for (const type of ['pointerdown', 'keydown']) {
    addEventListener(type, () => (used = true), {once: true, capture: true});
  }
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (hadController && !used) location.reload();
    else void offerUpdateIfNewer();
  });
}

/** How often an open page asks the server for a new version. */
const UPDATE_CHECK_MS = 24 * 60 * 60 * 1000;

/**
 * A home-screen app can stay open for days: ask the server whether there's
 * a new version (update.ts) when the page opens, whenever it comes back to
 * the front, and once a day.
 */
function watchForUpdates() {
  onNewVersion(offerUpdate);
  void offerUpdateIfNewer();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void offerUpdateIfNewer();
  });
  setInterval(() => void offerUpdateIfNewer(), UPDATE_CHECK_MS);
}

/** Asks the server; a new version makes the logo offer it (onNewVersion). */
async function offerUpdateIfNewer() {
  await newVersion();
}

/**
 * There's a new version: the logo becomes an update button. Tapping it
 * gets the new version and opens the home page in it (history and
 * everything else is saved, so nothing is lost).
 */
function offerUpdate() {
  const logo = document.querySelector<HTMLAnchorElement>('.logo');
  if (!logo || logo.classList.contains('update-ready')) return;
  logo.classList.add('update-ready');
  logo.title = t('New version: tap to update');
  logo.setAttribute('aria-label', t('New version: tap to update'));
  logo.addEventListener('click', e => {
    e.preventDefault();
    logo.classList.add('updating');
    logo.title = t('Updating…');
    void applyUpdate(logo.href);
  });
}

registerServiceWorker();
watchForUpdates();

// A dynamic import failing anywhere else (study pages, Anki, …).
window.addEventListener('unhandledrejection', e => reloadIfStale(e.reason));

main().catch(e => {
  if (reloadIfStale(e)) return;
  console.error(e);
  content.replaceChildren(
    h(
      'p',
      {class: 'error'},
      `${t("Couldn't load the dictionary data.")} `,
      h('a', {href: location.href}, t('Reload')),
    ),
  );
});
